import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { SessionProvider, useSession } from './session';
import { AppShell } from './layouts';

const Landing = React.lazy(() => import('./pages/Landing'));
const Payment = React.lazy(() => import('./pages/Payment'));
const OverviewPage = React.lazy(() => import('./pages/OverviewPage'));
const CallLogsPage = React.lazy(() => import('./pages/CallLogsPage'));
const CompleteSignup = React.lazy(() => import('./pages/CompleteSignup'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const ManageAgents = React.lazy(() => import('./pages/ManageAgents'));
const OpsPage = React.lazy(() => import('./pages/OpsPage'));
const OpsAccountDataPage = React.lazy(() => import('./pages/OpsAccountDataPage'));

function LoadingScreen({ message = 'Loading…' }: { message?: string }) {
  return (
    <div className="min-h-screen bg-surfaceAlt flex items-center justify-center px-4">
      <div
        className="tv-card w-full max-w-xs flex flex-col items-center gap-3 text-center py-6"
        role="status"
        aria-live="polite"
      >
        <div className="tv-spinner" aria-hidden />
        <p className="text-[15px] font-semibold text-ink">{message}</p>
      </div>
    </div>
  );
}

function RootPage() {
  const session = useSession();

  if (session.status === 'checking') return <LoadingScreen message="Verifying login…" />;
  if (session.status === 'unauth') return <Landing />;

  if (session.isActive) {
    return session.hasPhone && session.hasBilling
      ? <Navigate to="/app" replace />
      : <Navigate to="/completeSignup" replace />;
  }
  return <Navigate to="/completeSignup" replace />;
}

function RequireOnboardingLayout() {
  const session = useSession();
  const location = useLocation();

  if (session.status === 'checking') return <LoadingScreen message="Loading…" />;
  if (session.status === 'unauth') return <Navigate to="/" replace />;
  if (session.isActive && session.hasPhone && session.hasBilling) return <Navigate to="/app" replace />;

  const hasOwnerInfo = session.hasPhone
    && Boolean(session.account?.email)
    && Boolean(session.account?.name);
  const businessInfo = session.account?.businessInfo;
  const hasBusinessInfo = Boolean(businessInfo?.businessName)
    && Boolean(businessInfo?.country)
    && Boolean(businessInfo?.agentsRange)
    && Boolean(businessInfo?.expectedCallsPerMonth)
    && Boolean(businessInfo?.turnover)
    && Boolean(businessInfo?.businessEmail);

  if (location.pathname === '/billing') {
    if (!session.isActive) return <Navigate to="/completeSignup" replace />;
    if (session.hasBilling) return <Navigate to="/app" replace />;
    if (!hasOwnerInfo || !hasBusinessInfo) return <Navigate to="/completeSignup" replace />;
    return <AppShell showNav={false} />;
  }

  return <AppShell showNav={false} />;
}

function RequireRegisteredLayout() {
  const session = useSession();

  if (session.status === 'checking') return <LoadingScreen message="Loading…" />;
  if (session.status === 'unauth') return <Navigate to="/" replace />;
  if (!session.isActive) return <Navigate to="/completeSignup" replace />;
  if (!session.hasPhone || !session.hasBilling) return <Navigate to="/completeSignup" replace />;

  return <AppShell />;
}

export default function App() {
  return (
    <SessionProvider>
      <BrowserRouter>
        <React.Suspense fallback={<LoadingScreen message="Loading app…" />}>
          <Routes>
            <Route path="/" element={<RootPage />} />
            <Route element={<RequireOnboardingLayout />}>
              <Route path="/completeSetup" element={<NotFound />} />
              <Route path="/completeSignup" element={<CompleteSignup />} />
              <Route path="/billing" element={<Payment />} />
            </Route>
            <Route element={<RequireRegisteredLayout />}>
              <Route path="/app" element={<OverviewPage />} />
              <Route path="/calls" element={<CallLogsPage />} />
              <Route path="/agents" element={<ManageAgents />} />
              <Route path="/ops" element={<OpsPage />} />
              <Route path="/ops/account/:accountId" element={<OpsAccountDataPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </React.Suspense>
      </BrowserRouter>
    </SessionProvider>
  );
}
